import { Button, Chip, TraceLine, type TraceLineProps, useKey } from '@patchbay/ui'
import { Clock3, FlaskConical } from 'lucide-react'
import * as React from 'react'
import { createKeyedStore } from './chat-store'
import type {
  InvestigationResult,
  InvestigationStreamEvent,
  RileyFeedbackTarget,
  RileyRailDataSource,
  RileyRole,
  RileyUsage,
  StoredInvestigation,
} from './contracts'
import { RileyFeedback } from './feedback'

const SOURCE_LABEL: Record<string, string> = {
  conduit: 'Conduit',
  unifi: 'UniFi',
  pbx: 'PBX',
  ninja: 'Ninja',
  cambium: 'Cambium',
  upstream: 'Carrier status',
  desk: 'Desk',
  related: 'Related tickets',
  kb: 'Knowledge',
  crm: 'CRM',
  books: 'Books',
}

export interface InvestigationState {
  id: string | null
  status: 'idle' | 'queued' | 'running' | 'done' | 'failed'
  queuePosition: number | null
  trace: TraceLineProps[]
  result: InvestigationResult | null
  usage: RileyUsage | null
  capsHit: 'tool_calls' | 'seconds' | null
  seconds: number | null
  error: string | null
  /** The SSE connection ended; Patchbay's durable row is being checked for this exact id. */
  recovering: boolean
}

const INITIAL: InvestigationState = {
  id: null,
  status: 'idle',
  queuePosition: null,
  trace: [],
  result: null,
  usage: null,
  capsHit: null,
  seconds: null,
  error: null,
  recovering: false,
}

/** Investigations by case, outside React: a remount (expanding the rail) keeps the one in flight (patchbay#196). */
export const investigations = createKeyedStore<InvestigationState>(INITIAL)
const investigating = new Set<string>()

export function resetInvestigations(): void {
  investigations.reset()
  investigating.clear()
}

export const INVESTIGATION_POLL = { intervalMs: 4_000 }
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function stateFromStored(stored: StoredInvestigation): InvestigationState {
  let state: InvestigationState = {
    ...INITIAL,
    id: stored.id,
    status: stored.status,
    result: stored.result,
    usage: stored.usage,
    capsHit: stored.caps_hit,
    seconds: stored.seconds,
    error: stored.error?.detail ?? stored.error?.code ?? null,
    recovering: stored.status === 'queued' || stored.status === 'running',
  }
  for (const data of stored.trace) state = applyInvestigationEvent(state, { event: 'trace', data })
  return state
}

/** Poll only the UUID emitted for this invocation. A cached response for another run is ignored. */
async function recoverInvestigation(
  caseId: string,
  investigationId: string,
  getInvestigation: (id: string) => Promise<StoredInvestigation>,
): Promise<void> {
  investigations.set(caseId, (current) =>
    current.id === investigationId ? { ...current, status: 'running', error: null, recovering: true } : current,
  )
  while (true) {
    try {
      const stored = await getInvestigation(investigationId)
      if (stored.id !== investigationId) {
        await sleep(INVESTIGATION_POLL.intervalMs)
        continue
      }
      const next = stateFromStored(stored)
      investigations.set(caseId, (current) => (current.id === investigationId ? next : current))
      if (stored.status === 'done' || stored.status === 'failed') return
    } catch {
      // A transient GET failure is not evidence that Riley failed. Keep the real run visible and try again.
    }
    await sleep(INVESTIGATION_POLL.intervalMs)
  }
}

export function useInvestigation({
  caseId,
  dataSource,
}: {
  caseId: string
  dataSource: RileyRailDataSource
}): InvestigationState & { start: (question?: string) => Promise<void> } {
  const state = investigations.use(caseId)

  const start = React.useCallback(
    async (question?: string) => {
      if (investigating.has(caseId)) return
      investigating.add(caseId)
      investigations.set(caseId, () => ({ ...INITIAL, status: 'queued' }))
      let investigationId: string | null = null
      let acceptsFrames = true
      let terminal = false
      try {
        for await (const frame of dataSource.investigate(caseId, { question, max_tool_calls: 12, max_seconds: 180 })) {
          if (frame.event === 'started') {
            if (investigationId === null) investigationId = frame.data.investigation_id
            acceptsFrames = frame.data.investigation_id === investigationId
          }
          // Ignore a stale replay burst. Patchbay emits this invocation's id first, then validates Riley's id too.
          if (!acceptsFrames) continue
          investigations.set(caseId, (current) => applyInvestigationEvent(current, frame))
          if (frame.event === 'done' || frame.event === 'error') {
            terminal = true
            break
          }
        }
        if (!terminal) throw new TypeError('investigation stream ended before its result')
      } catch (reason) {
        const id = investigationId ?? investigations.get(caseId).id
        if (id && dataSource.getInvestigation) {
          await recoverInvestigation(caseId, id, dataSource.getInvestigation.bind(dataSource))
        } else {
          investigations.set(caseId, (current) => ({
            ...current,
            status: 'failed',
            error: reason instanceof Error ? reason.message : 'Investigation failed.',
            recovering: false,
          }))
        }
      } finally {
        investigating.delete(caseId)
      }
    },
    [caseId, dataSource],
  )

  return { ...state, start }
}

export function applyInvestigationEvent(state: InvestigationState, frame: InvestigationStreamEvent): InvestigationState {
  if (frame.event === 'queued') return { ...state, status: 'queued', queuePosition: frame.data.position }
  if (frame.event === 'started')
    return { ...state, id: frame.data.investigation_id, status: 'running', queuePosition: null, recovering: false }
  if (frame.event === 'trace') {
    const target = frame.data.target_label ? `${frame.data.verb} ${frame.data.target_label}` : frame.data.verb
    const detail = `${target} · ${frame.data.summary}${frame.data.ms === undefined ? '' : ` · ${frame.data.ms} ms`}`
    const line: TraceLineProps = {
      source: SOURCE_LABEL[frame.data.source] ?? frame.data.source,
      detail,
      status: frame.data.ok ? 'ok' : 'error',
      selfCitation: frame.data.citation?.type === 'riley_writeback',
    }
    return { ...state, trace: [...state.trace, line] }
  }
  if (frame.event === 'result') return { ...state, result: frame.data }
  if (frame.event === 'usage') return { ...state, usage: frame.data }
  if (frame.event === 'done') {
    return { ...state, status: 'done', capsHit: frame.data.caps_hit, seconds: frame.data.seconds, recovering: false }
  }
  return { ...state, status: 'failed', error: frame.data.detail, recovering: false }
}

export function InvestigateButton({ state, viewerRole }: { state: ReturnType<typeof useInvestigation>; viewerRole: RileyRole }) {
  const canRun = viewerRole !== 'billing'
  const inProgress = state.status === 'queued' || state.status === 'running'
  const run = React.useCallback(() => {
    if (canRun) void state.start()
  }, [canRun, state])

  useKey('i', run, { enabled: canRun && !inProgress }, [canRun, inProgress, run])
  if (!canRun) return null
  return (
    <Button variant="ghost" size="sm" title="Investigate (i)" pending={inProgress} onClick={run}>
      <FlaskConical /> Investigate
    </Button>
  )
}

export function InvestigationOutcome({
  state,
  viewerRole,
  onFeedback,
}: {
  state: InvestigationState
  viewerRole: RileyRole
  onFeedback?: (feedback: RileyFeedbackTarget) => Promise<void> | void
}) {
  if (state.status === 'idle') return null
  const id = state.id ?? 'pending-investigation'
  return (
    <section className="ml-9 rounded-lg border border-riley-border bg-riley-soft/40 px-3 py-2.5" aria-label="Investigation result">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-riley">Investigate</h3>
        <Chip tone="outline">≤12 reads</Chip>
        <Chip tone="outline">≤3 min run</Chip>
        {state.status === 'queued' ? <Chip tone="riley">Queued{state.queuePosition ? ` · ${state.queuePosition}` : ''}</Chip> : null}
        {state.status === 'running' ? <Chip tone="riley">Running{state.recovering ? ' · reconnecting' : ''}</Chip> : null}
        {state.status === 'failed' ? <Chip tone="danger">Failed</Chip> : null}
      </div>
      {state.result ? (
        <div className="mt-2 space-y-2 text-sm">
          <p className="whitespace-pre-wrap">{state.result.summary}</p>
          {state.result.key_facts.length ? (
            <ul className="list-disc pl-5">
              {state.result.key_facts.map((fact) => (
                <li key={fact}>{fact}</li>
              ))}
            </ul>
          ) : null}
          <p>
            <span className="font-medium">Next:</span> {state.result.recommendation}
          </p>
          <p className="text-xs text-muted-foreground">Confidence {Math.round(state.result.confidence * 100)}%</p>
        </div>
      ) : null}
      {state.error ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {state.error}
        </p>
      ) : null}
      {state.status === 'done' ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Clock3 className="size-3.5" /> {state.seconds?.toFixed(1) ?? '0.0'} s
          {state.capsHit ? <Chip tone="warning">Cap reached: {state.capsHit.replaceAll('_', ' ')}</Chip> : null}
          {viewerRole === 'manager' && state.usage ? (
            <span>
              Usage: {state.usage.requests ?? 0} request{state.usage.requests === 1 ? '' : 's'} · {state.usage.total ?? 0} tokens
            </span>
          ) : null}
          <span>Findings saved to Riley’s case context.</span>
          <div className="ml-auto">
            <RileyFeedback id={id} target="investigation" onFeedback={onFeedback} />
          </div>
        </div>
      ) : null}
    </section>
  )
}

export function InvestigationTrace({ trace }: { trace: TraceLineProps[] }) {
  return (
    <div className="divide-y divide-border">
      {trace.map((line, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: Riley's trace contract has no UI id.
        <TraceLine key={`${line.source}:${line.detail}:${index}`} {...line} />
      ))}
    </div>
  )
}
